import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';

const TYPE_LABEL = { pto: 'PTO', sick: 'Sick' };
const TYPE_COLOR = { pto: 'primary', sick: 'success' };

const EMPTY_ENTRY = { type: 'pto', date: '', endDate: '', hours: '', note: '' };

// Mirrors the backend's splitHoursAcrossDays() (timeTrackingRoutes.js) just for the live
// preview shown in the Log modal — the server always recomputes the actual split, this is
// display-only so the admin can see the per-day breakdown before saving.
function countWeekdays(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const days = [];
  const cur = new Date(`${startStr}T00:00:00Z`);
  const end = new Date(`${endStr}T00:00:00Z`);
  if (cur > end) return 0;
  while (cur <= end) {
    days.push(cur.getUTCDay());
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  const weekdays = days.filter(d => d !== 0 && d !== 6);
  return weekdays.length > 0 ? weekdays.length : days.length;
}

function tenureLabel(hiredAt) {
  if (!hiredAt) return '—';
  const ms = Date.now() - new Date(hiredAt).getTime();
  const years  = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  const months = Math.floor((ms % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
  if (years === 0) return `${months}mo`;
  return `${years}y ${months}mo`;
}

function BalanceCell({ bal, label, color }) {
  if (!bal) return <td className="text-muted small">—</td>;
  const hasCarryover  = bal.carryoverActive > 0;
  const hasUnpaid     = bal.unpaidHours > 0;
  return (
    <td>
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <span className={`fw-semibold ${bal.remaining === 0 && !hasUnpaid ? 'text-warning' : hasUnpaid ? 'text-danger' : 'text-success'}`}>
          {bal.remaining}h rem
        </span>
        {hasUnpaid && (
          <span className="badge bg-danger" title="Hours deducted from salary">+{bal.unpaidHours}h unpaid</span>
        )}
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {hasCarryover
          ? <span title={bal.carryoverExpiry ? `Carryover expires ${bal.carryoverExpiry}` : 'Carryover — no expiry'}>
              {bal.allocated}h alloc + {bal.carryoverActive}h carry · {bal.used}h used
            </span>
          : <>{bal.allocated}h alloc · {bal.used}h used</>
        }
        {bal.policyName && <span> · {bal.policyName}</span>}
      </div>
    </td>
  );
}

function EntryRow({ entry, onEdit, onDelete }) {
  return (
    <tr>
      <td>{entry.date}</td>
      <td><span className={`badge bg-${TYPE_COLOR[entry.type]}`}>{TYPE_LABEL[entry.type]}</span></td>
      <td>{entry.hours}h</td>
      <td className="text-muted small">{entry.note || '—'}</td>
      <td>
        <div className="d-flex gap-1">
          <button className="btn btn-xs btn-outline-secondary" style={{ fontSize: 11, padding: '1px 7px' }} onClick={() => onEdit(entry)}>Edit</button>
          <button className="btn btn-xs btn-outline-danger"    style={{ fontSize: 11, padding: '1px 7px' }} onClick={() => onDelete(entry)}>Del</button>
        </div>
      </td>
    </tr>
  );
}

export default function TimeOffDashboardPage() {
  const currentYear = new Date().getFullYear();
  const [year,     setYear]     = useState(currentYear);
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [expanded, setExpanded] = useState(null); // employeeId

  // Entries for expanded employee
  const [empEntries,   setEmpEntries]   = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Log entry modal
  const [logModal,    setLogModal]    = useState(null); // null | { employee, entry? }
  const [entryForm,   setEntryForm]   = useState(EMPTY_ENTRY);
  const [logSaving,   setLogSaving]   = useState(false);
  const [logError,    setLogError]    = useState('');
  const [overageWarn, setOverageWarn] = useState(false);

  const [confirmDel,  setConfirmDel]  = useState(null); // entry to delete
  const [deleting,    setDeleting]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const balData = await timeTrackingService.getTimeOffBalances({ year });
      setResults(balData.results || []);
    } catch {
      setError('Failed to load time off data');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const loadEntries = useCallback(async (employeeId) => {
    setEntriesLoading(true);
    try {
      const data = await timeTrackingService.getTimeOffEntries({ employeeId, year });
      setEmpEntries(data);
    } catch {
      setEmpEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [year]);

  const toggleExpand = (empId) => {
    if (expanded === empId) {
      setExpanded(null);
    } else {
      setExpanded(empId);
      loadEntries(empId);
    }
  };

  const openLog = (row, entry = null) => {
    setEntryForm(entry
      ? { type: entry.type, date: entry.date, endDate: entry.date, hours: entry.hours, note: entry.note || '' }
      : { ...EMPTY_ENTRY, date: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0] }
    );
    setOverageWarn(false);
    setLogError('');
    setLogModal({ row, entry });
  };

  // Compute whether the new hours would create overage
  const checkOverage = (form, row) => {
    const bal = form.type === 'pto' ? row.ptoBal : row.sickBal;
    if (!bal) return false;
    return Number(form.hours) > bal.remaining;
  };

  const handleFormChange = (field, val) => {
    setEntryForm(prev => {
      const next = { ...prev, [field]: val };
      // Keep endDate tracking date for the common single-day case — only stops tracking once
      // the admin deliberately picks a different end date to log a multi-day range.
      if (field === 'date' && !logModal?.entry && (!prev.endDate || prev.endDate === prev.date)) {
        next.endDate = val;
      }
      if (logModal) setOverageWarn(!logModal.entry && checkOverage(next, logModal.row));
      return next;
    });
  };

  const handleSaveEntry = async () => {
    if (!entryForm.date || !entryForm.hours) return setLogError('Date and hours are required.');
    if (entryForm.endDate && entryForm.endDate < entryForm.date) return setLogError('End date must be on or after the start date.');
    setLogSaving(true); setLogError('');
    try {
      if (logModal.entry) {
        await timeTrackingService.updateTimeOffEntry(logModal.entry.id, entryForm);
      } else {
        await timeTrackingService.createTimeOffEntry({
          ...entryForm,
          employeeId: logModal.row.employee.id,
          hours: Number(entryForm.hours),
        });
      }
      setLogModal(null);
      await load();
      if (expanded === logModal.row.employee.id) loadEntries(logModal.row.employee.id);
    } catch (err) {
      setLogError(err.response?.data?.error || 'Failed to save entry');
    } finally {
      setLogSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    setDeleting(true);
    try {
      await timeTrackingService.deleteTimeOffEntry(confirmDel.id);
      setConfirmDel(null);
      await load();
      if (expanded) loadEntries(expanded);
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="container mt-4">
      <TimeTrackingNav />

      <div className="d-flex align-items-center gap-3 mt-3 mb-3 flex-wrap">
        <h5 className="mb-0">Time Off</h5>
        <select
          className="form-select form-select-sm"
          style={{ width: 100 }}
          value={year}
          onChange={e => setYear(Number(e.target.value))}
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <Link to="/time-tracking/time-off/policies" className="btn btn-outline-secondary btn-sm ms-auto">
          Manage Policies →
        </Link>
      </div>

      {loading && <div className="text-muted">Loading…</div>}
      {error   && <div className="alert alert-danger">{error}</div>}

      {!loading && results.length === 0 && (
        <div className="alert alert-info">
          No employees found. <Link to="/admin/time-tracking">Add employees</Link> and assign PTO/Sick policies to see balances here.
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Hired</th>
                <th>Tenure</th>
                <th>PTO Balance</th>
                <th>Sick Balance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results.map(row => (
                <React.Fragment key={row.employee.id}>
                  <tr className={row.employee.status === 'terminated' ? 'text-muted' : ''}>
                    <td>
                      <button
                        className="btn btn-link btn-sm p-0 text-start fw-semibold"
                        onClick={() => toggleExpand(row.employee.id)}
                      >
                        {expanded === row.employee.id ? '▾' : '▸'} {row.employee.firstName} {row.employee.lastName}
                        {row.employee.status === 'terminated' && <span className="badge bg-secondary ms-1">Terminated</span>}
                      </button>
                    </td>
                    <td className="text-muted small">{row.employee.hiredAt || '—'}</td>
                    <td className="text-muted small">{tenureLabel(row.employee.hiredAt)}</td>
                    <BalanceCell bal={row.ptoBal}  label="PTO"  color="primary" />
                    <BalanceCell bal={row.sickBal} label="Sick" color="success" />
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openLog(row)}
                      >
                        + Log
                      </button>
                    </td>
                  </tr>
                  {/* Expanded entry list */}
                  {expanded === row.employee.id && (
                    <tr>
                      <td colSpan={6} className="bg-light p-0">
                        <div className="px-4 py-2">
                          {entriesLoading && <span className="text-muted small">Loading entries…</span>}
                          {!entriesLoading && empEntries.length === 0 && (
                            <span className="text-muted small">No entries for {year}.</span>
                          )}
                          {!entriesLoading && empEntries.length > 0 && (
                            <table className="table table-sm table-bordered mb-0" style={{ maxWidth: 600 }}>
                              <thead className="table-light">
                                <tr><th>Date</th><th>Type</th><th>Hours</th><th>Note</th><th></th></tr>
                              </thead>
                              <tbody>
                                {empEntries.map(e => (
                                  <EntryRow key={e.id} entry={e} onEdit={entry => openLog(row, entry)} onDelete={setConfirmDel} />
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Log entry modal */}
      {logModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {logModal.entry ? 'Edit Time Off' : `Log Time Off — ${logModal.row.employee.firstName} ${logModal.row.employee.lastName}`}
                </h5>
                <button className="btn-close" onClick={() => setLogModal(null)} />
              </div>
              <div className="modal-body">
                {logError && <div className="alert alert-danger py-2">{logError}</div>}
                {overageWarn && (
                  <div className="alert alert-warning py-2 small">
                    This employee has 0 h remaining — these hours will be deducted from salary.
                  </div>
                )}
                <div className="row g-3">
                  <div className={logModal.entry ? 'col-sm-4' : 'col-sm-3'}>
                    <label className="form-label small">Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={entryForm.type}
                      onChange={e => handleFormChange('type', e.target.value)}
                    >
                      <option value="pto">PTO</option>
                      <option value="sick">Sick</option>
                    </select>
                  </div>
                  <div className={logModal.entry ? 'col-sm-4' : 'col-sm-3'}>
                    <label className="form-label small">{logModal.entry ? 'Date' : 'Start Date'}</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={entryForm.date}
                      onChange={e => handleFormChange('date', e.target.value)}
                    />
                  </div>
                  {!logModal.entry && (
                    <div className="col-sm-3">
                      <label className="form-label small">End Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        min={entryForm.date || undefined}
                        value={entryForm.endDate}
                        onChange={e => handleFormChange('endDate', e.target.value)}
                      />
                    </div>
                  )}
                  <div className={logModal.entry ? 'col-sm-4' : 'col-sm-3'}>
                    <label className="form-label small">Hours{!logModal.entry && entryForm.endDate > entryForm.date ? ' (total)' : ''}</label>
                    <input
                      type="number" min="0.5" step="0.5"
                      className="form-control form-control-sm"
                      value={entryForm.hours}
                      onChange={e => handleFormChange('hours', e.target.value)}
                    />
                  </div>
                  {!logModal.entry && entryForm.endDate > entryForm.date && entryForm.hours && (
                    <div className="col-12">
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Split evenly across {countWeekdays(entryForm.date, entryForm.endDate)} weekday(s):{' '}
                        ≈ {(Number(entryForm.hours) / countWeekdays(entryForm.date, entryForm.endDate)).toFixed(2)}h/day.
                        Weekend days in the range get 0h.
                      </div>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label small">Note <span className="text-muted">(optional)</span></label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={entryForm.note}
                      onChange={e => handleFormChange('note', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setLogModal(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveEntry} disabled={logSaving}>
                  {logSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete entry confirm modal */}
      {confirmDel && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <div style={{ fontSize: '2rem' }}>🗑️</div>
                <div className="fw-semibold mt-2">Delete this entry?</div>
                <div className="text-muted small mt-1">
                  {confirmDel.type?.toUpperCase()} · {confirmDel.date} · {confirmDel.hours}h
                  {confirmDel.note ? ` — ${confirmDel.note}` : ''}
                </div>
              </div>
              <div className="modal-footer justify-content-center gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={handleDeleteEntry} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
